import React, { Fragment } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import ChevronLeftIcon from '@atlaskit/icon/utility/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import AkChevronLeft from '@atlaskit/icon/glyph/chevron-left-circle';
import AkChevronRight from '@atlaskit/icon/glyph/chevron-right-circle';
import { N0, N90 } from '@atlaskit/theme/colors';

import { fg } from '@confluence/feature-gating';

import type { OnboardingSlide } from './OnboardingModal';
import { ModalImage } from './ModalImage';

// Global constants
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBody = styled.div({
	textAlign: 'center',
	padding: token('space.600', '48px'),
	height: '100px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledImageWrapper = styled.div<{
	isTransparent?: boolean;
}>({
	width: '100%',
	height: '225px',
	maxHeight: '225px',
	position: 'relative',
	borderTopLeftRadius: '3px',
	borderTopRightRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isTransparent
			? 'transparent'
			: `${token('color.background.accent.teal.subtle', '#60c6d2')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OldStyledChevronButton = styled.button({
	width: '30px',
	cursor: 'pointer',
	background: 0,
	padding: 0,
	outline: 0,
	border: 0,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '100px',
	opacity: 0.7,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledChevronButton = styled(OldStyledChevronButton)({
	height: '30px',
	borderRadius: '50%',
	backgroundColor: token('color.background.accent.gray.bolder'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledFlexedButtons = styled.div({
	height: '100%',
	display: 'flex',
	flexDirection: 'row',
	padding: `${token('space.0', '0')} ${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButtonLeft = styled.div({
	textAlign: 'left',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButtonRight = styled.div({
	textAlign: 'right',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledHeading = styled.h2({
	font: token('font.heading.medium'),
	marginBottom: token('space.250', '20px'),
});

type OnboardingModalBodyProps = {
	selectedIndex: number;
	isLastSlide: boolean;
	isFirstSlide: boolean;
	slides: OnboardingSlide[];
	onPrev: () => void;
	onNext: () => void;
};

export function OnboardingModalBody({
	selectedIndex,
	isFirstSlide,
	isLastSlide,
	onPrev,
	onNext,
	slides,
}: OnboardingModalBodyProps) {
	const { title, body, imageUrlLight, imageUrlDark, isTransparentBackground } =
		slides[selectedIndex];

	const renderChevrons = () => (
		<StyledFlexedButtons>
			<StyledButtonLeft>
				{!isFirstSlide &&
					(fg('platform-visual-refresh-icons') ? (
						<OldStyledChevronButton onClick={onPrev}>
							{/* Disabling while icons are being rolled out */}
							{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
							<AkChevronLeft
								label="previous-slide-chevron"
								size="large"
								primaryColor={token('color.icon.subtle', N90)}
							/>
						</OldStyledChevronButton>
					) : (
						<StyledChevronButton onClick={onPrev}>
							<ChevronLeftIcon
								label="previous-slide-chevron"
								color={token('color.icon.inverse', N0)}
							/>
						</StyledChevronButton>
					))}
			</StyledButtonLeft>
			<StyledButtonRight>
				{!isLastSlide &&
					(fg('platform-visual-refresh-icons') ? (
						<OldStyledChevronButton onClick={onNext}>
							{/* Disabling while icons are being rolled out */}
							{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
							<AkChevronRight
								label="next-slide-chevron"
								size="large"
								primaryColor={token('color.icon.subtle', N90)}
							/>
						</OldStyledChevronButton>
					) : (
						<StyledChevronButton onClick={onNext}>
							<ChevronRightIcon
								label="next-slide-chevron"
								color={token('color.icon.inverse', N0)}
							/>
						</StyledChevronButton>
					))}
			</StyledButtonRight>
		</StyledFlexedButtons>
	);

	return (
		<Fragment>
			<StyledImageWrapper isTransparent={isTransparentBackground}>
				<ModalImage src={imageUrlLight} srcDark={imageUrlDark} />
				{renderChevrons()}
			</StyledImageWrapper>
			<StyledBody>
				<StyledHeading>{title}</StyledHeading>
				<div>{body}</div>
			</StyledBody>
		</Fragment>
	);
}
